import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {Button, Text, Stack, Group} from '@mantine/core';
import {Dropzone} from '@mantine/dropzone';
import React, {useRef, useState} from 'react';

import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {ActionMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  StructureWithFormat,
  getStructureWithFormatFromFile,
} from 'notebook/integrations/chemistry/reaction-scheme/ReactionSketcherModal';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export default function EmptySchemePlaceholder({
  locked = false,
  openSketcherModalWithStructureAndFormat,
}: {
  locked?: boolean;
  openSketcherModalWithStructureAndFormat: (
    structureWithFormat: StructureWithFormat,
    actionMethod: ActionMethod
  ) => void;
}): JSX.Element {
  const fileSelectorOpenRef = useRef<() => void>(noop);
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Dropzone
      {...getTestProps({component: 'EmptySchemePlaceholder', element: 'dropzone'})}
      multiple={false}
      disabled={locked}
      openRef={fileSelectorOpenRef}
      onDrop={async (files: Array<File>): Promise<void> => {
        const file = files[0];
        try {
          setIsLoading(true);
          const structureWithFormat = await getStructureWithFormatFromFile(file);
          if (structureWithFormat != null) {
            openSketcherModalWithStructureAndFormat(structureWithFormat, ActionMethod.UPLOAD);
          }
        } finally {
          setIsLoading(false);
        }
      }}
      loading={isLoading}
      styles={{
        inner: {
          pointerEvents: 'all',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        root: {
          height: '100%',
          ...(locked ? {cursor: 'not-allowed'} : {}),
        },
      }}
    >
      {!locked && (
        <Stack align="center" spacing="md">
          <Group spacing="sm">
            <BenchlingIcon
              icon="paperclip"
              style={{width: '24px', height: '24px', color: Color.GRAY_600}}
              altText="Paperclip"
            />
            <Text size="lg" color="dimmed">
              Drag file in, click to select, or click to start sketching
            </Text>
          </Group>

          <Stack align="center" spacing={8}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                fileSelectorOpenRef.current();
              }}
            >
              Select file
            </Button>

            <Text size="sm" color="dimmed">
              or
            </Text>

            <Button
              // eslint-disable-next-line no-restricted-syntax
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                openSketcherModalWithStructureAndFormat(
                  {structure: null, format: MarvinChemicalFormat.MRV},
                  ActionMethod.BUTTON_CLICK
                );
              }}
              leftIcon={<BenchlingIcon icon="edit" />}
            >
              Start by sketching
            </Button>
          </Stack>
        </Stack>
      )}
    </Dropzone>
  );
}
